import React, { useState } from 'react';
import {
  Card,
  Space,
  Slider,
  Button,
  DatePicker,
  Collapse,
  Tag,
  Statistic,
  Row,
  Col,
  Table,
  Badge,
  Tooltip,
  Alert,
  Empty,
  Select,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, ClusterOutlined, BulbOutlined } from '@ant-design/icons';
import { PageTitle } from '@/shared/components/PageTitle';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/shared/lib/auth-context';
import {
  useGetApiAdminSearchHistoryClusters,
  usePostApiAdminSearchHistoryGenerateGuideDraft,
  usePostApiAdminGuides,
  type SearchHistorySummary,
  type GuideUpsertRequest
} from '@/features/guides/api/generated';
import { useGuideForm } from '@/features/guides/hooks/useGuideForm';
import { GenerateGuideDrawer } from '@/features/guides/components/GenerateGuideDrawer';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { getDefaultSourceLocale } from '@/shared/config/localeConfig';

const { RangePicker } = DatePicker;
const { Panel } = Collapse;

type DateRangePreset = 'day' | 'week' | 'month' | 'custom';

const formatStockholmTime = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Stockholm',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
};

const getSimilarityColor = (similarity: number): string => {
  if (similarity >= 0.8) return '#52c41a'; // green
  if (similarity >= 0.65) return '#faad14'; // yellow/orange
  return '#ff4d4f'; // red
};

export const SearchClustersPage: React.FC = () => {
  const { t } = useTranslation();
  const { token, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Filter state
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('week');
  const [customDateRange, setCustomDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [maxClusters, setMaxClusters] = useState<number>(10);
  const [minClusterSize, setMinClusterSize] = useState<number>(3);
  const [similarityThreshold, setSimilarityThreshold] = useState<number>(0.75);

  // Search parameters snapshot (only updated when user clicks search)
  const [searchParams, setSearchParams] = useState<{
    maxClusters: number;
    minClusterSize: number;
    similarityThreshold: number;
    dateRange: { startDate: string; endDate: string } | null;
  } | null>(null);

  // Generate guide drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [generateError, setGenerateError] = useState<Error | null>(null);

  // Guide form hook for drawer
  const guideForm = useGuideForm({
    defaultSourceLocale: getDefaultSourceLocale()
  });

  // Calculate date range based on preset or custom
  const getDateRange = (): { startDate: string; endDate: string } | null => {
    const now = dayjs();
    let startDate: Dayjs;
    let endDate: Dayjs = now;

    if (dateRangePreset === 'custom' && customDateRange) {
      startDate = customDateRange[0];
      endDate = customDateRange[1];
    } else {
      switch (dateRangePreset) {
        case 'day':
          startDate = now.subtract(1, 'day');
          break;
        case 'week':
          startDate = now.subtract(7, 'day');
          break;
        case 'month':
          startDate = now.subtract(30, 'day');
          break;
        default:
          return null;
      }
    }

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  };

  const {
    data: clusterResponse,
    isLoading,
    error,
  } = useGetApiAdminSearchHistoryClusters(
    {
      ...(searchParams?.dateRange && {
        StartDate: searchParams.dateRange.startDate,
        EndDate: searchParams.dateRange.endDate,
      }),
      MaxClusters: searchParams?.maxClusters ?? 10,
      MinClusterSize: searchParams?.minClusterSize ?? 3,
      SimilarityThreshold: searchParams?.similarityThreshold ?? 0.75,
    },
    {
      query: {
        enabled: !!token && isAuthenticated && !authLoading && !!searchParams,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        staleTime: Infinity,
      },
    }
  );

  const handleSearch = () => {
    const dateRange = getDateRange();
    setSearchParams({
      maxClusters,
      minClusterSize,
      similarityThreshold,
      dateRange,
    });
  };

  // Generate guide draft mutation
  const generateGuideDraftMutation = usePostApiAdminSearchHistoryGenerateGuideDraft({
    mutation: {
      onSuccess: (response) => {
        setGenerateError(null);
        if (response.guide) {
          // Pre-populate form with generated guide data
          const rootVehicle = response.guide.rootVehicle || {};
          const filters = response.guide.filters || [];

          guideForm.resetForm({
            content: {
              problem: response.guide.content?.problem || '',
              possibleCause: response.guide.content?.possibleCause || '',
              solutions: response.guide.content?.solutions || [''],
            },
            rootVehicle: {
              manufacturer: rootVehicle.manufacturer,
              modelSeries: rootVehicle.modelSeries,
              modelYear: rootVehicle.modelYear,
              engineBaseType: rootVehicle.engineBaseType as any,
              euroCode: rootVehicle.euroCode,
            },
            filters: filters.map(filter => ({
              manufacturer: filter.manufacturer,
              modelSeries: filter.modelSeries,
              modelYearMin: filter.modelYearMin,
              modelYearMax: filter.modelYearMax,
              engineBaseType: filter.engineBaseType as any,
              euroCode: filter.euroCode,
            })),
            sourceLocale: response.guide.sourceLocale || getDefaultSourceLocale()
          });
          setDrawerOpen(true);
        }
      },
      onError: (error) => {
        setGenerateError(error as Error);
        message.error(t('clusters.results.generateError'));
      }
    }
  });

  // Create guide mutation
  const createGuideMutation = usePostApiAdminGuides({
    mutation: {
      onSuccess: (response) => {
        message.success(t('guides.created'));
        setDrawerOpen(false);
        if (response.guide?.id) {
          navigate(`/guides/${response.guide.id}`);
        }
      },
      onError: () => {
        message.error(t('guides.createError'));
      }
    }
  });

  // Handle generate guide for a cluster
  const handleGenerateGuide = (searchIds: string[]) => {
    setGenerateError(null);
    generateGuideDraftMutation.mutate({
      data: {
        searchIds,
        locale: getDefaultSourceLocale()
      }
    });
  };

  // Handle save guide from drawer
  const handleSaveGuide = async () => {
    const payload: GuideUpsertRequest = {
      sourceLocale: guideForm.formState.sourceLocale,
      content: guideForm.formState.content,
      rootVehicle: guideForm.formState.rootVehicle,
      filters: guideForm.formState.filters,
      status: 'Draft'
    };

    createGuideMutation.mutate({ data: payload });
  };

  // Table columns for representative searches
  const searchColumns: ColumnsType<SearchHistorySummary> = [
    {
      title: String(t('clusters.results.problem')),
      dataIndex: 'problem',
      key: 'problem',
      width: '40%',
      render: (text: string) => (
        <Tooltip title={text}>
          <div
            style={{
              maxWidth: 400,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {text}
          </div>
        </Tooltip>
      ),
    },
    {
      title: String(t('clusters.results.userName')),
      dataIndex: ['user', 'name'],
      key: 'userName',
      width: '20%',
      render: (name: string) => name || '-',
    },
    {
      title: String(t('clusters.results.requestedAt')),
      dataIndex: 'requestedAt',
      key: 'requestedAt',
      width: '20%',
      render: (date: string) => (date ? formatStockholmTime(date) : '-'),
    },
    {
      title: String(t('clusters.results.resultCount')),
      dataIndex: 'resultCount',
      key: 'resultCount',
      width: '10%',
      align: 'center',
    },
    {
      title: String(t('clusters.results.locale')),
      dataIndex: 'locale',
      key: 'locale',
      width: '10%',
      render: (locale: string) => <Tag color="blue">{locale?.toUpperCase()}</Tag>,
    },
  ];

  return (
    <div>
      <PageTitle title={String(t('clusters.title'))} subtitle={String(t('clusters.subtitle'))} />

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* @ts-ignore Filter Panel */}
        <div>
          <Row gutter={[16, 16]}>
            {/* Date Range Selection */}
            <Col xs={24} sm={8}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                {String(t('clusters.filters.dateRange'))}
              </div>
              <Select
                value={dateRangePreset}
                onChange={(value) => setDateRangePreset(value)}
                style={{ width: '100%' }}
                options={[
                  { value: 'day', label: String(t('clusters.filters.last24Hours')) },
                  { value: 'week', label: String(t('clusters.filters.last7Days')) },
                  { value: 'month', label: String(t('clusters.filters.last30Days')) },
                  { value: 'custom', label: String(t('clusters.filters.customRange')) },
                ]}
              />
            </Col>
            <Col xs={24} sm={16}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                {String(t('clusters.filters.customDateRange'))}
              </div>
              <RangePicker
                showTime
                value={customDateRange}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setCustomDateRange([dates[0], dates[1]]);
                    setDateRangePreset('custom');
                  }
                }}
                placeholder={[
                  String(t('clusters.filters.startDate')),
                  String(t('clusters.filters.endDate')),
                ]}
                style={{ width: '100%' }}
                disabled={dateRangePreset !== 'custom'}
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            {/* Max Clusters Slider */}
            <Col xs={24} md={8}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                {String(t('clusters.filters.maxClusters'))}: {maxClusters}
              </div>
              <Slider
                min={1}
                max={100}
                value={maxClusters}
                onChange={setMaxClusters}
                marks={{ 1: '1', 50: '50', 100: '100' }}
                styles={{
                  track: { backgroundColor: '#52c41a' },
                }}
              />
            </Col>

            {/* Min Cluster Size Slider */}
            <Col xs={24} md={8}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                {String(t('clusters.filters.minClusterSize'))}: {minClusterSize}
              </div>
              <Slider
                min={2}
                max={100}
                value={minClusterSize}
                onChange={setMinClusterSize}
                marks={{ 2: '2', 50: '50', 100: '100' }}
                styles={{
                  track: { backgroundColor: '#52c41a' },
                }}
              />
            </Col>

            {/* Similarity Threshold Slider */}
            <Col xs={24} md={8}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                {String(t('clusters.filters.similarityThreshold'))}: {similarityThreshold.toFixed(2)}
              </div>
              <Slider
                min={0.5}
                max={1.0}
                step={0.05}
                value={similarityThreshold}
                onChange={setSimilarityThreshold}
                marks={{ 0.5: '0.50', 0.75: '0.75', 1.0: '1.00' }}
                styles={{
                  track: { backgroundColor: '#52c41a' },
                }}
              />
            </Col>
          </Row>

          {/* Search Button */}
          <Row style={{ marginTop: 24 }}>
            <Col span={24}>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                size="large"
                block
              >
                {String(t('clusters.filters.search'))}
              </Button>
            </Col>
          </Row>
        </div>

        {/* Error Display */}
        {error && (
          <Alert
            message={String(t('clusters.results.error'))}
            description={String(t('clusters.results.errorDescription'))}
            type="error"
            showIcon
          />
        )}

        {/* Loading State */}
        {isLoading && (
          <Card loading={true}>
            <div style={{ minHeight: 200 }} />
          </Card>
        )}

        {/* Results */}
        {!isLoading && clusterResponse && (
          <div style={{ display: 'contents' }}>
            {/* Statistics */}
            <Card>
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title={String(t('clusters.results.totalSearches'))}
                    value={clusterResponse.totalSearchesAnalyzed}
                    prefix={<SearchOutlined />}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title={String(t('clusters.results.clustersFound'))}
                    value={clusterResponse.metadata?.clustersReturned || 0}
                    prefix={<ClusterOutlined />}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title={String(t('clusters.results.dateRange'))}
                    value={
                      clusterResponse.startDate && clusterResponse.endDate
                        ? `${dayjs(clusterResponse.startDate).format('YYYY-MM-DD')} - ${dayjs(clusterResponse.endDate).format('YYYY-MM-DD')}`
                        : '-'
                    }
                  />
                </Col>
              </Row>
            </Card>

            {/* Clusters */}
            {clusterResponse.clusters && clusterResponse.clusters.length > 0 ? (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {clusterResponse.clusters.map((cluster, index) => (
                  <Card
                    key={index}
                    title={
                      <Space>
                        <ClusterOutlined />
                        {String(t('clusters.results.cluster'))} {index + 1}
                        <Badge
                          count={cluster.clusterSize}
                          style={{ backgroundColor: '#1890ff' }}
                        />
                      </Space>
                    }
                    extra={
                      <Space>
                        <Tooltip title={String(t('clusters.results.averageSimilarity'))}>
                          <Tag
                            color={getSimilarityColor(cluster.averageSimilarity || 0)}
                            style={{ fontSize: 14, padding: '4px 8px' }}
                          >
                            {((cluster.averageSimilarity || 0) * 100).toFixed(0)}%
                          </Tag>
                        </Tooltip>
                        <Button
                          type="primary"
                          icon={<BulbOutlined />}
                          loading={generateGuideDraftMutation.isPending}
                          onClick={() => {
                            const searchIds = cluster.representativeSearches
                              ?.map(search => search.id)
                              .filter((id): id is string => !!id) || [];
                            if (searchIds.length > 0) {
                              handleGenerateGuide(searchIds);
                            }
                          }}
                        >
                          {String(t('clusters.results.generateGuide'))}
                        </Button>
                      </Space>
                    }
                  >
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      {/* Common Problems */}
                      {cluster.commonProblems && cluster.commonProblems.length > 0 && (
                        <div>
                          <div
                            style={{
                              marginBottom: 8,
                              fontWeight: 500,
                              fontSize: 14,
                            }}
                          >
                            {String(t('clusters.results.commonProblems'))}:
                          </div>
                          <Space wrap>
                            {cluster.commonProblems.map((problem, idx) => (
                              <Tag key={idx} color="blue">
                                {problem}
                              </Tag>
                            ))}
                          </Space>
                        </div>
                      )}

                      {/* Representative Searches */}
                      {cluster.representativeSearches &&
                        cluster.representativeSearches.length > 0 && (
                          <Collapse>
                            <Panel
                              header={String(t('clusters.results.representativeSearches'))}
                              key="1"
                            >
                              <Table
                                columns={searchColumns}
                                dataSource={cluster.representativeSearches.map(
                                  (search, idx) => ({
                                    ...search,
                                    key: search.id || `search-${idx}`,
                                  })
                                )}
                                pagination={false}
                                size="small"
                              />
                            </Panel>
                          </Collapse>
                        )}
                    </Space>
                  </Card>
                ))}
              </Space>
            ) : (
              <Card>
                <Empty description={String(t('clusters.results.noClusters'))} />
              </Card>
            )}
          </div>
        )}

        {/* Initial State - No search yet */}
        {!isLoading && !clusterResponse && !error && (
          <Card>
            <Empty
              description={String(t('clusters.results.noSearchYet'))}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </Card>
        )}
      </Space>

      {/* Generate Guide Drawer */}
      <GenerateGuideDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setGenerateError(null);
        }}
        formState={guideForm.formState}
        isDirty={guideForm.isDirty}
        isCreating={createGuideMutation.isPending}
        isGenerating={generateGuideDraftMutation.isPending}
        generateError={generateError}
        createNewGuide={handleSaveGuide}
        updateContentField={guideForm.updateContentField}
        updateRootVehicleWithSelection={guideForm.updateRootVehicleWithSelection}
        addSolution={guideForm.addSolution}
        updateSolution={guideForm.updateSolution}
        removeSolution={guideForm.removeSolution}
        reorderSolutions={guideForm.reorderSolutions}
        addFilter={guideForm.addFilter}
        removeFilterAt={guideForm.removeFilterAt}
        updateFilterAt={guideForm.updateFilterAt}
      />
    </div>
  );
};
